import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from '../../components/layout/PublicNavBar';
import HeroSection from '../../components/landing/HeroSection';
import FeatureGrid from '../../components/landing/FeatureGrid';
import { Button } from '../../components/ui/button';
import { Container } from '../../components/ui/container';

const FEATURE_DEFS = [
  { icon: 'ri-qr-code-line', titleKey: 'landing.feature_qr_title', descKey: 'landing.feature_qr_desc' },
  { icon: 'ri-file-pdf-2-line', titleKey: 'landing.feature_pdf_title', descKey: 'landing.feature_pdf_desc' },
  { icon: 'ri-pen-nib-line', titleKey: 'landing.feature_sketch_title', descKey: 'landing.feature_sketch_desc' },
  { icon: 'ri-sticky-note-line', titleKey: 'landing.feature_notes_title', descKey: 'landing.feature_notes_desc' },
  { icon: 'ri-share-line', titleKey: 'landing.feature_share_title', descKey: 'landing.feature_share_desc' },
  { icon: 'ri-dashboard-line', titleKey: 'landing.feature_dashboard_title', descKey: 'landing.feature_dashboard_desc' },
];

export default function Landing() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleGetStarted = () => navigate('/login');

  const metaTitle = t('landing.meta_title');
  const metaDescription = t('landing.meta_description');
  const ogDescription = t('landing.og_description');

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/40">
      <Helmet htmlAttributes={{ lang: i18n.language }}>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content="https://qpressen.com/og-image.png" />
        <meta property="og:url" content="https://qpressen.com/landing" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={i18n.language === 'ko' ? 'ko_KR' : 'en_US'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <link rel="canonical" href="https://qpressen.com/landing" />
      </Helmet>
      <PublicNavBar
        logoLabel={t('landing.nav_logo')}
        ctaLabel={t('landing.nav_cta')}
        onCta={handleGetStarted}
      />

      <HeroSection
        title={t('landing.hero_title')}
        subtitle={t('landing.hero_subtitle')}
        ctaLabel={t('landing.hero_cta')}
        onCta={handleGetStarted}
        videoSrc="/demo.mp4"
        videoPoster="/demo-poster.jpg"
      />

      {/* 기능 소개 섹션 */}
      <section className="bg-card py-16 sm:py-24">
        <Container>
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-2xl font-bold text-foreground sm:text-3xl">
              {t('landing.features_title')}
            </h2>
            <p className="text-muted-foreground">{t('landing.features_subtitle')}</p>
          </div>

          <FeatureGrid
            items={FEATURE_DEFS.map((f) => ({
              iconClass: f.icon,
              title: t(f.titleKey),
              description: t(f.descKey),
            }))}
            columns={isMobile ? 1 : 3}
          />
        </Container>
      </section>

      {/* CTA 섹션 */}
      <section className="py-16 sm:py-24">
        <Container>
          <div className="rounded-2xl bg-gradient-to-r from-violet-500 to-purple-500 px-6 py-12 text-center sm:px-12 sm:py-16">
            <h2 className="mb-4 text-2xl font-bold text-white sm:text-3xl">
              {t('landing.cta_title')}
            </h2>
            <p className="mx-auto mb-8 max-w-xl text-violet-100">
              {t('landing.cta_subtitle')}
            </p>
            <Button
              type="button"
              size="xl"
              variant="secondary"
              className="bg-white text-violet-600 hover:bg-violet-50"
              onClick={handleGetStarted}
            >
              {t('landing.cta_button')}
            </Button>
          </div>
        </Container>
      </section>

      {/* 푸터 */}
      <footer className="border-t border-border py-8 safe-bottom">
        <Container className="text-center">
          <p className="text-sm text-muted-foreground">{t('landing.footer_copyright')}</p>
        </Container>
      </footer>
    </div>
  );
}
